import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Search, Plus, Upload, Pencil, Trash2, Loader2 } from "lucide-react";

const EMPTY = { name: "", code: "", ico: "", city: "", street: "", zip: "", phone: "" };

export default function Customers() {
  const [data, setData] = useState({ customers: [], total: 0 });
  const [search, setSearch] = useState("");
  const [dialog, setDialog] = useState(null); // {mode, form}
  const [uploading, setUploading] = useState(false);

  const load = useCallback(() => {
    api.get("/customers", { params: search ? { search } : {} }).then((r) => setData(r.data));
  }, [search]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const save = async () => {
    const { mode, form } = dialog;
    if (!form.name.trim()) { toast.error("Zadejte název"); return; }
    if (mode === "new") await api.post("/customers", form);
    else await api.put(`/customers/${form.id}`, form);
    toast.success("Uloženo");
    setDialog(null);
    load();
  };

  const del = async (id) => { await api.delete(`/customers/${id}`); toast.success("Smazáno"); load(); };

  const onImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/customers/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Naimportováno ${r.data.imported} odběratelů`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Import se nezdařil");
    } finally { setUploading(false); e.target.value = ""; }
  };

  return (
    <div className="fade-up">
      <PageHeader title="Odběratelé" subtitle={`${data.total} odběratelů v databázi`}>
        <label className="inline-flex">
          <input type="file" accept=".xlsx,.xls" className="hidden" onChange={onImport} data-testid="customers-import-input" />
          <Button variant="outline" size="sm" asChild className="rounded-full cursor-pointer" data-testid="customers-import-btn">
            <span>{uploading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />} Import Excel</span>
          </Button>
        </label>
        <Button size="sm" className="rounded-full" data-testid="customer-new-btn"
          onClick={() => setDialog({ mode: "new", form: { ...EMPTY } })}>
          <Plus className="h-4 w-4 mr-2" /> Nový odběratel
        </Button>
      </PageHeader>

      <div className="px-10 py-8 space-y-5">
        <div className="relative max-w-md">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="customers-search" placeholder="Hledat název, IČO, město…" value={search}
            onChange={(e) => setSearch(e.target.value)} className="pl-9 bg-white" />
        </div>

        <div className="bg-white border border-border rounded-xl overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/60 hover:bg-muted/60">
                <TableHead>Název</TableHead>
                <TableHead className="w-28">Kód</TableHead>
                <TableHead className="w-32">IČO</TableHead>
                <TableHead>Město</TableHead>
                <TableHead>Ulice</TableHead>
                <TableHead className="w-24">PSČ</TableHead>
                <TableHead className="w-24"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.customers.length === 0 && (
                <TableRow><TableCell colSpan={7} className="text-center py-12 text-muted-foreground">
                  Žádní odběratelé. Naimportujte Excel nebo přidejte ručně.
                </TableCell></TableRow>
              )}
              {data.customers.map((c) => (
                <TableRow key={c.id} data-testid={`customer-row-${c.id}`}>
                  <TableCell className="font-medium">{c.name}</TableCell>
                  <TableCell className="num text-sm text-muted-foreground">{c.code || "—"}</TableCell>
                  <TableCell className="num text-sm">{c.ico || "—"}</TableCell>
                  <TableCell className="text-sm">{c.city || "—"}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{c.street || "—"}</TableCell>
                  <TableCell className="num text-sm">{c.zip || "—"}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1 justify-end">
                      <button onClick={() => setDialog({ mode: "edit", form: { ...EMPTY, ...c } })}
                        data-testid={`customer-edit-${c.id}`} className="text-muted-foreground hover:text-foreground p-1.5">
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <button data-testid={`customer-delete-${c.id}`} className="text-muted-foreground hover:text-destructive p-1.5">
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Smazat odběratele?</AlertDialogTitle>
                            <AlertDialogDescription>„{c.name}" bude odstraněn.</AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Zrušit</AlertDialogCancel>
                            <AlertDialogAction onClick={() => del(c.id)}>Smazat</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        {data.total > data.customers.length && (
          <p className="text-xs text-muted-foreground">Zobrazeno {data.customers.length} z {data.total}. Použijte hledání.</p>
        )}
      </div>

      <Dialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display">
            {dialog?.mode === "new" ? "Nový odběratel" : "Upravit odběratele"}
          </DialogTitle></DialogHeader>
          {dialog && (
            <div className="space-y-3 py-1">
              <div>
                <Label>Název *</Label>
                <Input data-testid="customer-form-name" className="mt-1.5" value={dialog.form.name}
                  onChange={(e) => setDialog({ ...dialog, form: { ...dialog.form, name: e.target.value } })} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Kód</Label><Input className="mt-1.5" value={dialog.form.code || ""}
                  onChange={(e) => setDialog({ ...dialog, form: { ...dialog.form, code: e.target.value } })} /></div>
                <div><Label>IČO</Label><Input className="mt-1.5" value={dialog.form.ico || ""}
                  onChange={(e) => setDialog({ ...dialog, form: { ...dialog.form, ico: e.target.value } })} /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Město</Label><Input className="mt-1.5" value={dialog.form.city || ""}
                  onChange={(e) => setDialog({ ...dialog, form: { ...dialog.form, city: e.target.value } })} /></div>
                <div><Label>Ulice</Label><Input className="mt-1.5" value={dialog.form.street || ""}
                  onChange={(e) => setDialog({ ...dialog, form: { ...dialog.form, street: e.target.value } })} /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>PSČ</Label><Input className="mt-1.5" value={dialog.form.zip || ""}
                  onChange={(e) => setDialog({ ...dialog, form: { ...dialog.form, zip: e.target.value } })} /></div>
                <div><Label>Telefon</Label><Input className="mt-1.5" value={dialog.form.phone || ""}
                  onChange={(e) => setDialog({ ...dialog, form: { ...dialog.form, phone: e.target.value } })} /></div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(null)}>Zrušit</Button>
            <Button onClick={save} data-testid="customer-form-save">Uložit</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
